/**
 * Dashboard.jsx - Main projection table page.
 *
 * The first thing a logged-in user sees. Shows the current week's ML
 * projections, lets users filter/sort/search, and links out to Compare,
 * Analytics, and Team Builder. Favorites sync to both localStorage and the
 * backend so they persist across devices.
 */

import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Tooltip from '../components/Tooltip';
import { api } from '../utils/api';
import { ChevronDown, Search, X, Check, AlertCircle, Heart, RefreshCw, Star } from 'lucide-react';

// --- Favorites helpers (localStorage fallback) ---

// These three functions keep the favorites list in localStorage so the UI
// feels instant even before the API responds.
const _localFavs = () => {
  try { return JSON.parse(localStorage.getItem('favoritePlayers') || '[]'); }
  catch { return []; }
};
const _localFavAdd = (player) => {
  try {
    const cur = _localFavs();
    if (!cur.find(p => p.id === player.id))
      localStorage.setItem('favoritePlayers', JSON.stringify([...cur, { id: player.id, name: player.name, pos: player.pos, team: player.team }]));
  } catch {}
};
const _localFavRemove = (id) => {
  try { localStorage.setItem('favoritePlayers', JSON.stringify(_localFavs().filter(p => p.id !== id))); }
  catch {}
};

// Exported so other pages (e.g. Compare) can read the current favorites list
export const getFavorites   = _localFavs;
export const toggleFavorite = (player) => {
  const cur     = _localFavs();
  const exists  = cur.find(p => p.id === player.id);
  const updated = exists ? cur.filter(p => p.id !== player.id) : [...cur, { id: player.id, name: player.name, pos: player.pos, team: player.team }];
  localStorage.setItem('favoritePlayers', JSON.stringify(updated));
  return updated;
};

// --- Risk rating helper ---

/**
 * Converts a confidence interval into a simple Low / Med / High risk label.
 * Uses both the absolute spread (in points) and the spread as a percentage
 * of the projection so that a 10-point spread means something different for
 * a 5-point and a 30-point projection.
 */
const getRisk = (ciLow, ciHigh, proj) => {
  if (ciLow == null || ciHigh == null) return { label: '—', color: 'text-white/30', bg: 'bg-white/[0.03]', dot: 'bg-white/20' };
  const spread = ciHigh - ciLow;
  const pct    = proj > 0 ? (spread / proj) * 100 : 0;
  if (spread > 18 || pct > 150) return { label: 'High', color: 'text-red-400',    bg: 'bg-red-500/10',    dot: 'bg-red-400'    };
  if (spread > 10 || pct > 100) return { label: 'Med',  color: 'text-yellow-400', bg: 'bg-yellow-500/10', dot: 'bg-yellow-400' };
  return                                { label: 'Low',  color: 'text-green-400',  bg: 'bg-green-500/10',  dot: 'bg-green-400'  };
};

const formatCI = (val) => val != null ? Number(val).toFixed(1) : '—';

// Returns a headshot URL for skill players, or an ESPN team logo for DST
const getPlayerImage = (player) => {
  if (player.imageUrl) return player.imageUrl;
  if (player.pos === 'DST' && player.team && player.team !== '—')
    return `https://a.espncdn.com/i/teamlogos/nfl/500/${player.team.toLowerCase()}.png`;
  return null;
};

// Tailwind class pairs for each position badge
const POS_COLORS = {
  QB:  'bg-red-500/20 text-red-400',
  RB:  'bg-green-500/20 text-green-400',
  WR:  'bg-blue-500/20 text-blue-400',
  TE:  'bg-yellow-500/20 text-yellow-400',
  K:   'bg-purple-500/20 text-purple-400',
  DST: 'bg-orange-500/20 text-orange-400',
};
const posColor = (pos) => POS_COLORS[pos] || 'bg-white/10 text-white/60';

const Dashboard = () => {
  const navigate = useNavigate();

  // Season and week are auto-detected from the API, not hardcoded
  const [season,          setSeason]          = useState(null);
  const [selectedWeek,    setSelectedWeek]    = useState(null);
  const [availableWeeks,  setAvailableWeeks]  = useState([]);
  const [weeksLoading,    setWeeksLoading]    = useState(true);

  // --- Data state ---
  const [players,         setPlayers]         = useState([]);
  const [loading,         setLoading]         = useState(false);
  const [error,           setError]           = useState(null);
  const [lastUpdated,     setLastUpdated]      = useState(null);

  // Used to scroll the table into view when position filter changes
  const tableRef = useRef(null);

  // --- Filter / sort / search state ---
  const [activeTab,       setActiveTab]        = useState('All');
  const [sortBy,          setSortBy]           = useState('proj');
  const [sortDir,         setSortDir]          = useState('desc');
  const [searchQuery,     setSearchQuery]      = useState('');
  const [page,            setPage]             = useState(1);
  const PAGE_SIZE = 50;

  // --- UI state ---
  const [selectedPlayers, setSelectedPlayers]  = useState([]);   // IDs checked for compare
  const [viewingPlayer,   setViewingPlayer]    = useState(null); // player detail card
  const [favoritedIds,    setFavoritedIds]     = useState(() => _localFavs().map(p => p.id));
  const [favToast,        setFavToast]         = useState(null);

  // Injury status from Sleeper: keyed by lowercase player name
  const [playerStatuses,  setPlayerStatuses]   = useState({});

  // --- Step 1: determine which season/week to show on load ---
  useEffect(() => {
    setWeeksLoading(true);
    api.getAvailableWeeks()
      .then(weeks => {
        if (!weeks || weeks.length === 0) {
          // No predictions in DB at all — fall back gracefully
          setSeason(2026);
          setSelectedWeek(1);
          setAvailableWeeks([]);
          return;
        }
        // Sort descending so index 0 is the most recent week
        const sorted = [...weeks].sort((a, b) =>
          b.season !== a.season ? b.season - a.season : b.week - a.week
        );
        const latest = sorted[0];
        setSeason(latest.season);
        setSelectedWeek(latest.week);
        setAvailableWeeks(sorted);
      })
      .catch(() => {
        // API error — still render the page with empty state
        setSeason(2026);
        setSelectedWeek(1);
        setAvailableWeeks([]);
      })
      .finally(() => setWeeksLoading(false));
  }, []);

  // Season-level accuracy summary (only meaningful for completed seasons)
  const [accuracy, setAccuracy] = useState(null);

  useEffect(() => {
    if (season != null && season < 2026) {
      api.getSeasonAccuracy(season)
        .then(data => setAccuracy(data?.mae != null ? data : null))
        .catch(() => setAccuracy(null));
    } else {
      setAccuracy(null);
    }
  }, [season]);

  // --- Step 2: fetch projections whenever the selected week or position changes ---
  const fetchProjections = useCallback(async () => {
    if (season == null || selectedWeek == null) return;
    setLoading(true);
    setError(null);
    try {
      const position = activeTab === 'All' ? null : activeTab;
      const data = await api.getProjections({ season, week: selectedWeek, position });
      const normalised = (data.players || []).map(p => ({
        id:       p.id,
        name:     p.name,
        pos:      p.pos,
        team:     p.team,
        proj:     p.proj,
        ciLow:    p.ciLow  != null ? p.ciLow  : null,
        ciHigh:   p.ciHigh != null ? p.ciHigh : null,
        imageUrl: p.imageUrl || '',
        actual:   p.actual  != null ? p.actual : null,
        diff:     p.diff    != null ? p.diff   : null,
        opp:      '—',
      }));
      setPlayers(normalised);
      setLastUpdated(new Date().toLocaleTimeString());
    } catch (err) {
      setError(err.message || 'Failed to load projections');
    } finally {
      setLoading(false);
    }
  }, [season, selectedWeek, activeTab]);

  useEffect(() => { fetchProjections(); }, [fetchProjections]);

  // Fetch injury statuses from Sleeper — non-critical, silently ignore failures
  useEffect(() => {
    api.getPlayerStatuses()
      .then(data => { if (data?.players) setPlayerStatuses(data.players); })
      .catch(() => {});
  }, []);

  // Sync favorites from the backend on load; fall back to localStorage on error
  useEffect(() => {
    api.getFavorites()
      .then(favs => {
        if (!favs) return;
        setFavoritedIds(favs.map(f => f.player_id));
        localStorage.setItem('favoritePlayers', JSON.stringify(
          favs.map(f => ({ id: f.player_id, name: f.player_name, pos: f.position, team: f.team }))
        ));
      })
      .catch(() => setFavoritedIds(_localFavs().map(p => p.id)));
  }, []);

  // --- Derived data ---

  // Apply search, sort, and position filter all in one pass
  const displayedPlayers = useMemo(() => players
    .filter(p => !searchQuery || p.name.toLowerCase().includes(searchQuery.toLowerCase()))
    .sort((a, b) => {
      if (sortBy === 'proj')    return sortDir === 'desc' ? b.proj - a.proj : a.proj - b.proj;
      if (sortBy === 'name')    return sortDir === 'desc' ? b.name.localeCompare(a.name) : a.name.localeCompare(b.name);
      if (sortBy === 'floor')   return sortDir === 'desc' ? (b.ciLow ?? 0) - (a.ciLow ?? 0) : (a.ciLow ?? 0) - (b.ciLow ?? 0);
      if (sortBy === 'ceiling') return sortDir === 'desc' ? (b.ciHigh ?? 0) - (a.ciHigh ?? 0) : (a.ciHigh ?? 0) - (b.ciHigh ?? 0);
      if (sortBy === 'risk') {
        const order = { Low: 0, Med: 1, High: 2 };
        const ra = order[getRisk(a.ciLow, a.ciHigh, a.proj).label] ?? -1;
        const rb = order[getRisk(b.ciLow, b.ciHigh, b.proj).label] ?? -1;
        return sortDir === 'desc' ? rb - ra : ra - rb;
      }
      return 0;
    }), [players, searchQuery, sortBy, sortDir]);

  // Only render the first N rows; "Load more" increments the page
  const pagedPlayers = useMemo(
    () => displayedPlayers.slice(0, page * PAGE_SIZE),
    [displayedPlayers, page, PAGE_SIZE]
  );
  const hasMore = pagedPlayers.length < displayedPlayers.length;

  const trending  = [...players].sort((a, b) => b.proj - a.proj).slice(0, 3);
  const firstName = localStorage.getItem('user_first_name') || 'User';

  // Derive which seasons/weeks are available from the fetched list
  const availableSeasons = [...new Set(availableWeeks.map(w => w.season))].sort((a, b) => b - a);
  const weeksForSeason   = availableWeeks.filter(w => w.season === season).map(w => w.week).sort((a, b) => a - b);

  // --- Event handlers ---

  const handleToggleFavorite = async (player) => {
    const isFav = favoritedIds.includes(player.id);
    // Optimistic update — revert on API error
    setFavoritedIds(isFav ? favoritedIds.filter(id => id !== player.id) : [...favoritedIds, player.id]);
    setFavToast(isFav ? 'removed' : 'added');
    setTimeout(() => setFavToast(null), 2500);
    try {
      if (isFav) { _localFavRemove(player.id); await api.removeFavorite(player.id); }
      else        { _localFavAdd(player);       await api.addFavorite(player.id); }
    } catch { setFavoritedIds(favoritedIds); }
  };

  // Allow up to 3 players to be selected for comparison
  const handleSelectPlayer = (id) => {
    setSelectedPlayers(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : prev.length < 3 ? [...prev, id] : prev
    );
  };

  const handleSort = (col) => {
    if (sortBy === col) setSortDir(d => d === 'desc' ? 'asc' : 'desc');
    else { setSortBy(col); setSortDir('desc'); }
    setPage(1);
  };

  const handleSeasonChange = (newSeason) => {
    setSeason(newSeason);
    // Auto-select the most recent week available in the new season
    const weeksInSeason = availableWeeks.filter(w => w.season === newSeason).map(w => w.week).sort((a,b) => b - a);
    if (weeksInSeason.length > 0) setSelectedWeek(weeksInSeason[0]);
  };

  // Show a spinner while we wait for the available-weeks API
  if (weeksLoading) {
    return (
      <div className="min-h-screen bg-[#050d1f] text-white font-sans">
        <Navbar/>
        <div className="flex items-center justify-center h-[80vh] gap-3 text-white/30">
          <RefreshCw size={18} className="animate-spin"/>
          <span>Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050d1f] text-white font-sans pb-24 relative overflow-x-hidden">

      {/* Favorite added/removed toast */}
      <div className={`fixed top-5 left-1/2 -translate-x-1/2 z-[200] transition-all duration-300 ${favToast ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-3 pointer-events-none'}`}>
        <div className={`flex items-center gap-2 text-sm font-semibold px-4 py-2.5 rounded-2xl shadow-xl border bg-[#0a0a0a] ${favToast === 'added' ? 'border-[#4d7cff]/30 text-[#4d7cff]' : 'border-red-500/30 text-red-400'}`}>
          <Heart size={13} className={favToast === 'added' ? 'fill-[#4d7cff]' : ''} />
          {favToast === 'added' ? 'Added to favorites' : 'Removed from favorites'}
        </div>
      </div>

      <Navbar />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 pt-6 sm:pt-8">

        {/* Page header */}
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-8 gap-4">
          <div>
            <h1 className="text-3xl font-bold mb-1">
              Welcome back, <span className="text-[#4d7cff]">{firstName}</span>
            </h1>
            <p className="text-white/30 text-sm">
              {season != null && selectedWeek != null
                ? `${season} Season · Week ${selectedWeek}`
                : 'Loading...'}
              {lastUpdated && <span className="ml-2 text-white/20">· Updated {lastUpdated}</span>}
            </p>
          </div>
          <button
            onClick={() => navigate('/analytics')}
            className="flex items-center gap-2 bg-[#4d7cff] hover:bg-[#3d6cef] text-white text-sm font-bold py-2.5 px-6 rounded-xl transition-all shadow-[0_4px_14px_rgba(77,124,255,0.25)] w-fit group"
          >
            View Analytics
            <ChevronDown size={15} className="-rotate-90 group-hover:translate-x-0.5 transition-transform" />
          </button>
        </div>

        {/* Summary stat cards */}
        <div data-tour="summary-cards" className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          {[
            { label: 'Total Projections',  value: players.length,                                                                                                                   color: 'text-blue-400',   border: 'border-blue-500/20',   glow: 'bg-blue-500'   },
            { label: 'Low Risk Plays',     value: players.length ? players.filter(p => getRisk(p.ciLow, p.ciHigh, p.proj).label === 'Low').length + ' players' : '—',             color: 'text-cyan-400',   border: 'border-cyan-500/20',   glow: 'bg-cyan-500'   },
            { label: 'Top Projection',     value: players.length ? `${players[0]?.name?.split(' ')[1] || players[0]?.name} · ${Math.max(...players.map(p=>p.proj)).toFixed(1)}` : '—', color: 'text-indigo-400', border: 'border-indigo-500/20', glow: 'bg-indigo-500' },
          ].map((stat, i) => (
            <div key={i} className={`bg-[#0c1526] border ${stat.border} rounded-2xl p-5 relative overflow-hidden`}>
              <div className={`absolute -right-8 -top-8 w-24 h-24 rounded-full ${stat.glow} opacity-10 blur-2xl`}></div>
              <div className={`text-3xl font-black mb-1.5 ${stat.color} truncate`}>{stat.value}</div>
              <div className="text-white/35 text-xs font-medium uppercase tracking-wider">{stat.label}</div>
            </div>
          ))}
        </div>

        {/* Model accuracy banner — only shown for seasons with actual data */}
        {accuracy && (
          <div data-tour="accuracy-banner" className="bg-[#0d1730] border border-[#4d7cff]/25 rounded-2xl px-5 py-4 mb-6 flex flex-wrap items-center gap-6">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-[#4d7cff] animate-pulse"></div>
              <span className="text-xs font-bold text-white/50 uppercase tracking-wider">Model Accuracy — 2025 Season</span>
            </div>
            <div className="flex gap-6">
              <div>
                <span className="text-xl font-black text-[#4d7cff]">{accuracy.mae}</span>
                <span className="text-xs text-white/30 ml-1.5">avg error (pts)</span>
              </div>
              <div>
                <span className="text-xl font-black text-green-400">{accuracy.within_5}%</span>
                <span className="text-xs text-white/30 ml-1.5">within 5 pts</span>
              </div>
              <div>
                <span className="text-xl font-black text-white/50">{accuracy.n}</span>
                <span className="text-xs text-white/30 ml-1.5">players compared</span>
              </div>
            </div>
          </div>
        )}

        {/* Top-3 trending players */}
        {trending.length > 0 && (
          <div data-tour="trending" className="mb-8">
            <div className="flex items-center gap-2 mb-4">
              <h2 className="text-base font-bold text-white/60 uppercase tracking-wider">Trending</h2>
              <div className="flex-1 h-px bg-white/[0.05]"></div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {trending.map((player, i) => (
                <div
                  key={player.id}
                  onClick={() => { setViewingPlayer(player); }}
                  className="relative group h-40 rounded-2xl overflow-hidden border border-white/[0.07] cursor-pointer hover:border-[#4d7cff]/30 transition-all"
                >
                  <div className="absolute inset-0 bg-gradient-to-t from-[#020617] via-[#020617]/60 to-transparent z-10"></div>
                  {getPlayerImage(player) ? (
                    <img src={getPlayerImage(player)} alt={player.name} className="absolute inset-0 w-full h-full object-cover opacity-50 group-hover:opacity-70 group-hover:scale-105 transition-all duration-500" />
                  ) : (
                    <div className="absolute inset-0 bg-gradient-to-br from-[#4d7cff]/10 to-transparent flex items-center justify-center text-4xl font-black text-white/5">
                      {player.name.split(' ').map(n=>n[0]).join('')}
                    </div>
                  )}
                  {i === 0 && (
                    <div className="absolute top-3 left-3 z-20">
                      <div className="flex items-center gap-1 bg-[#4d7cff]/90 text-white text-[9px] font-black px-2 py-0.5 rounded-full uppercase tracking-wider">
                        <Star size={8} className="fill-white" /> Top Pick
                      </div>
                    </div>
                  )}
                  <div className="absolute bottom-0 left-0 w-full p-4 z-20 flex justify-between items-end">
                    <div>
                      <div className="flex items-center gap-1.5 mb-0.5">
                        <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded ${posColor(player.pos)}`}>{player.pos}</span>
                        <span className="text-[10px] text-white/40">{player.team}</span>
                      </div>
                      <div className="font-bold text-sm leading-tight">{player.name}</div>
                    </div>
                    <div className="text-right">
                      <div className="text-xl font-black text-[#4d7cff]">{player.proj}</div>
                      <div className="text-[9px] text-white/30 uppercase tracking-wider">proj pts</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Filter bar */}
        <div ref={tableRef} className="bg-[#0c1526] border border-white/[0.10] rounded-t-2xl px-5 py-4 flex flex-col md:flex-row gap-4 items-center flex-wrap">

          {/* Position filter tabs */}
          <div data-tour="pos-filters" className="flex bg-white/[0.04] p-0.5 rounded-xl gap-0.5 flex-wrap">
            {['All', 'QB', 'RB', 'WR', 'TE', 'K', 'DST'].map(pos => (
              <button key={pos} onClick={() => { setActiveTab(pos); setPage(1); tableRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }); }}
                className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${activeTab === pos ? 'bg-[#4d7cff] text-white shadow-sm' : 'text-white/40 hover:text-white'}`}>
                {pos}
              </button>
            ))}
          </div>

          {/* Season selector */}
          {availableSeasons.length > 0 && (
            <div className="flex items-center gap-2">
              <span className="text-white/30 text-xs font-medium">Season</span>
              <div className="flex bg-white/[0.04] p-0.5 rounded-xl gap-0.5">
                {availableSeasons.map(s => (
                  <button key={s} onClick={() => handleSeasonChange(s)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${season === s ? 'bg-[#4d7cff] text-white' : 'text-white/40 hover:text-white'}`}>
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Week selector — pill buttons on desktop, dropdown on mobile */}
          <div className="flex items-center gap-2">
            <span className="text-white/30 text-xs font-medium">Week</span>
            {weeksForSeason.length > 0 ? (
              <>
                <div className="hidden sm:flex bg-white/[0.04] p-0.5 rounded-xl gap-0.5 flex-wrap max-w-xs">
                  {weeksForSeason.map(w => (
                    <button key={w} onClick={() => setSelectedWeek(w)}
                      className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${selectedWeek === w ? 'bg-[#4d7cff] text-white' : 'text-white/40 hover:text-white'}`}>
                      {w}
                    </button>
                  ))}
                </div>
                <select value={selectedWeek || ''} onChange={e => setSelectedWeek(Number(e.target.value))}
                  className="sm:hidden bg-white/[0.04] border border-white/[0.08] rounded-lg px-3 py-1.5 text-xs text-white focus:outline-none focus:border-[#4d7cff]/40 cursor-pointer">
                  {weeksForSeason.map(w => (
                    <option key={w} value={w}>Week {w}</option>
                  ))}
                </select>
              </>
            ) : (
              // Fallback when no weeks exist in the DB
              <select value={selectedWeek || 1} onChange={e => setSelectedWeek(Number(e.target.value))}
                className="bg-white/[0.04] border border-white/[0.08] rounded-lg px-3 py-1.5 text-xs text-white focus:outline-none focus:border-[#4d7cff]/40 cursor-pointer">
                {Array.from({length:18},(_,i)=>i+1).map(w=>(
                  <option key={w} value={w}>Week {w}</option>
                ))}
              </select>
            )}
          </div>

          {/* Sort controls */}
          <div className="flex items-center gap-2">
            <span className="text-white/30 text-xs font-medium">Sort</span>
            {[{key:'proj',label:'Pts'},{key:'name',label:'Name'}].map(s=>(
              <button key={s.key} onClick={() => handleSort(s.key)}
                className={`px-3 py-1.5 rounded-lg text-xs font-bold border transition-all flex items-center gap-1 ${sortBy===s.key ? 'border-[#4d7cff]/40 text-[#4d7cff] bg-[#4d7cff]/10' : 'border-white/[0.08] text-white/40 hover:text-white'}`}>
                {s.label} {sortBy===s.key&&(sortDir==='desc'?'↓':'↑')}
              </button>
            ))}
          </div>

          {/* Player name search */}
          <div className="relative flex-1 md:max-w-xs">
            <Search size={12} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/25" />
            <input type="text" placeholder="Search player..." value={searchQuery}
              onChange={e => { setSearchQuery(e.target.value); setPage(1); }}
              className="w-full bg-white/[0.04] border border-white/[0.08] rounded-lg pl-8 pr-8 py-1.5 text-xs text-white placeholder-white/25 focus:outline-none focus:border-[#4d7cff]/40"/>
            {searchQuery && (
              <button onClick={()=>setSearchQuery('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-white/25 hover:text-white"><X size={11}/></button>
            )}
          </div>

          <button onClick={fetchProjections} className="text-white/25 hover:text-white transition-colors ml-auto">
            <RefreshCw size={14} />
          </button>
        </div>

        {/* Projection table */}
        <div data-tour="player-table" className="bg-[#080d1a] border border-white/[0.10] border-t-0 rounded-b-2xl overflow-hidden min-h-[200px]">

          {loading && (
            <div className="flex items-center justify-center py-16 gap-2.5 text-white/30">
              <RefreshCw size={16} className="animate-spin"/>
              <span className="text-sm">Loading projections...</span>
            </div>
          )}

          {error && !loading && (
            <div className="flex flex-col items-center justify-center py-16 gap-3 text-red-400">
              <AlertCircle size={20}/>
              <p className="text-sm">{error}</p>
              <button onClick={fetchProjections} className="text-xs text-white/30 hover:text-white underline">Try again</button>
            </div>
          )}

          {!loading && !error && displayedPlayers.length === 0 && (
            <div className="flex flex-col items-center justify-center py-16 gap-2 text-white/20">
              <p className="text-base font-medium">No projections available</p>
              <p className="text-xs">
                {season && selectedWeek
                  ? `No data for ${season} Week ${selectedWeek}. Run generate_predictions.py to populate.`
                  : 'Try a different week or position'}
              </p>
            </div>
          )}

          {!loading && !error && displayedPlayers.length > 0 && (
            <>
            <div className="overflow-x-auto rounded-2xl border border-white/[0.08] bg-[#080d1a]">
              <table className="w-full">
              <thead>
                <tr className="border-b border-white/[0.08] bg-white/[0.03]">
                  <th className="w-8 px-4 py-3.5"></th>
                  <th className="px-4 py-3.5 text-left text-[10px] text-white/40 uppercase tracking-widest font-bold">Player</th>
                  <th className="px-3 sm:px-4 py-3.5 text-left text-[10px] text-white/40 uppercase tracking-widest font-bold hidden sm:table-cell">Pos</th>
                  <th className="px-3 sm:px-4 py-3.5 text-left text-[10px] text-white/40 uppercase tracking-widest font-bold cursor-pointer hover:text-white/70 select-none" onClick={() => handleSort('proj')}>
                    <span className="flex items-center gap-0.5">
                      Proj {sortBy === 'proj' && (sortDir === 'desc' ? '↓' : '↑')}
                      <Tooltip text="Estimated fantasy points for this week, generated by our ML model using historical stats, matchup difficulty, and player trends." />
                    </span>
                  </th>
                  {accuracy && <th className="px-3 sm:px-4 py-3.5 text-left text-[10px] text-green-400/70 uppercase tracking-widest font-bold hidden sm:table-cell">Actual</th>}
                  {accuracy && <th className="px-3 sm:px-4 py-3.5 text-left text-[10px] text-white/40 uppercase tracking-widest font-bold hidden sm:table-cell">Diff</th>}
                  <th className="px-3 sm:px-4 py-3.5 text-left text-[10px] text-white/40 uppercase tracking-widest font-bold hidden md:table-cell cursor-pointer hover:text-white/70 select-none" onClick={() => handleSort('floor')}>
                    Floor {sortBy === 'floor' && (sortDir === 'desc' ? '↓' : '↑')}
                  </th>
                  <th className="px-3 sm:px-4 py-3.5 text-left text-[10px] text-white/40 uppercase tracking-widest font-bold hidden md:table-cell cursor-pointer hover:text-white/70 select-none" onClick={() => handleSort('ceiling')}>
                    Ceiling {sortBy === 'ceiling' && (sortDir === 'desc' ? '↓' : '↑')}
                  </th>
                  <th className="px-3 sm:px-4 py-3.5 text-left text-[10px] text-white/40 uppercase tracking-widest font-bold cursor-pointer hover:text-white/70 select-none" onClick={() => handleSort('risk')}>
                    <span className="flex items-center gap-0.5">
                      Risk {sortBy === 'risk' && (sortDir === 'desc' ? '↓' : '↑')}
                      <Tooltip text="How unpredictable this projection is. Low = confident estimate. High = wide range between floor and ceiling." />
                    </span>
                  </th>
              </tr>
              </thead>
                <tbody className="divide-y divide-white/[0.05]">
                  {pagedPlayers.map((player, idx) => {
                    const risk       = getRisk(player.ciLow, player.ciHigh, player.proj);
                    const isSelected = selectedPlayers.includes(player.id);
                    const isFav      = favoritedIds.includes(player.id);
                    const status     = playerStatuses[player.name.toLowerCase()];
                    return (
                      <tr key={player.id} className={`group transition-colors cursor-pointer ${isSelected ? 'bg-[#4d7cff]/[0.06]' : idx % 2 === 0 ? 'bg-transparent hover:bg-white/[0.03]' : 'bg-white/[0.01] hover:bg-white/[0.03]'}`}>
                        <td className="px-4 py-3.5" onClick={e => e.stopPropagation()}>
                          <button onClick={() => handleSelectPlayer(player.id)}
                            className={`w-4 h-4 rounded border flex items-center justify-center transition-all ${isSelected ? 'bg-[#4d7cff] border-[#4d7cff]' : 'border-white/20 hover:border-[#4d7cff]/50'}`}>
                            {isSelected && <Check size={9} strokeWidth={3}/>}
                          </button>
                        </td>
                        <td className="px-4 py-3.5" onClick={() => { setViewingPlayer(player); }}>
                          <div className="flex items-center gap-3">
                            <div className="w-9 h-9 rounded-xl bg-[#4d7cff]/20 border border-[#4d7cff]/20 overflow-hidden flex-shrink-0 flex items-center justify-center text-[10px] font-bold text-[#4d7cff]">
                              {getPlayerImage(player)
                                ? <img src={getPlayerImage(player)} alt={player.name} loading="lazy" className="w-full h-full object-cover" onError={e=>{e.target.style.display='none';}}/>
                                : player.name.split(' ').map(n=>n[0]).join('')}
                            </div>
                            <div>
                              <div className="flex items-center gap-1.5">
                                <span className="font-semibold text-sm text-white leading-tight group-hover:text-[#4d7cff] transition-colors">{player.name}</span>
                                {status?.injury_label && (
                                  <span className={`text-[9px] font-black px-1.5 py-0.5 rounded border border-current/20 ${status.injury_color}`}>
                                    {status.injury_label}
                                  </span>
                                )}
                              </div>
                              <div className="text-[10px] text-white/40 mt-0.5">{player.team}</div>
                            </div>
                            <button onClick={e => { e.stopPropagation(); handleToggleFavorite(player); }}
                              className={`ml-0.5 transition-all ${isFav ? 'opacity-100' : 'opacity-0 group-hover:opacity-60'}`}>
                              <Heart size={11} className={isFav ? 'fill-[#4d7cff] text-[#4d7cff]' : 'text-white/40 hover:text-[#4d7cff]'}/>
                            </button>
                          </div>
                        </td>
                        <td className="px-3 sm:px-4 py-3.5 hidden sm:table-cell" onClick={() => { setViewingPlayer(player); }}>
                          <span className={`text-[10px] font-bold px-2.5 py-1 rounded-lg ${posColor(player.pos)}`}>{player.pos}</span>
                        </td>
                        <td className="px-3 sm:px-4 py-3.5" onClick={() => { setViewingPlayer(player); }}>
                          <span className="text-lg font-black text-[#4d7cff]">{player.proj}</span>
                        </td>
                        {accuracy && (
                          <td className="px-3 sm:px-4 py-3.5 hidden sm:table-cell" onClick={() => { setViewingPlayer(player); }}>
                            <span className="text-base font-black text-green-400">
                              {player.actual != null ? player.actual : <span className="text-white/25 text-xs">—</span>}
                            </span>
                          </td>
                        )}
                        {accuracy && (
                          <td className="px-3 sm:px-4 py-3.5 hidden sm:table-cell" onClick={() => { setViewingPlayer(player); }}>
                            {player.diff != null ? (
                              <span className={`text-xs font-bold font-mono ${
                                Math.abs(player.diff) <= 3  ? 'text-green-400'  :
                                Math.abs(player.diff) <= 7  ? 'text-yellow-400' :
                                                              'text-red-400'
                              }`}>
                                {player.diff > 0 ? '+' : ''}{player.diff}
                              </span>
                            ) : <span className="text-white/25 text-xs">—</span>}
                          </td>
                        )}
                        <td className="px-3 sm:px-4 py-3.5 text-sm text-white/55 font-mono hidden md:table-cell" onClick={() => { setViewingPlayer(player); }}>{formatCI(player.ciLow)}</td>
                        <td className="px-3 sm:px-4 py-3.5 text-sm text-white/55 font-mono hidden md:table-cell" onClick={() => { setViewingPlayer(player); }}>{formatCI(player.ciHigh)}</td>
                        <td className="px-3 sm:px-4 py-3.5" onClick={() => { setViewingPlayer(player); }}>
                          <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-bold ${risk.bg} ${risk.color}`}>
                            <div className={`w-1.5 h-1.5 rounded-full ${risk.dot}`}></div>
                            {risk.label}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {hasMore && (
              <div className="flex justify-center pt-4 pb-2">
                <button
                  onClick={() => setPage(p => p + 1)}
                  className="px-6 py-2.5 rounded-xl bg-white/[0.05] border border-white/[0.08] text-sm text-white/60 hover:text-white hover:bg-white/10 transition-all"
                >
                  Load more ({displayedPlayers.length - pagedPlayers.length} remaining)
                </button>
              </div>
            )}
            </>
          )}
        </div>
      </main>

      {/* Floating compare bar — appears when 1+ players are selected */}
      {selectedPlayers.length > 0 && (
        <div className="fixed bottom-4 sm:bottom-6 left-1/2 -translate-x-1/2 z-50 w-[calc(100vw-2rem)] sm:w-auto max-w-lg">
          <div className="bg-[#0d0d0d] border border-white/15 rounded-2xl px-4 sm:px-5 py-3 flex items-center gap-3 sm:gap-5 shadow-[0_8px_40px_rgba(0,0,0,0.7)] backdrop-blur-md flex-wrap justify-between">
            <div className="flex items-center gap-2">
              {selectedPlayers.map(id => {
                const p = displayedPlayers.find(pl => pl.id === id);
                return p ? (
                  <div key={id} className="flex items-center gap-1.5 bg-white/[0.05] rounded-xl px-2.5 py-1.5">
                    <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded ${posColor(p.pos)}`}>{p.pos}</span>
                    <span className="text-xs font-medium">{p.name.split(' ')[1] || p.name}</span>
                    <button onClick={() => handleSelectPlayer(id)} className="text-white/25 hover:text-white ml-0.5"><X size={10}/></button>
                  </div>
                ) : null;
              })}
            </div>
            <button onClick={() => navigate('/compare', { state: { playerIds: selectedPlayers } })}
              disabled={selectedPlayers.length < 2}
              className="bg-[#4d7cff] hover:bg-[#3d6cef] disabled:opacity-40 text-white text-xs font-bold px-4 py-2 rounded-xl transition-all whitespace-nowrap">
              Compare {selectedPlayers.length < 2 ? `(${selectedPlayers.length}/2)` : '→'}
            </button>
          </div>
        </div>
      )}

      {/* Player detail card (modal) */}
      {viewingPlayer && (() => {
        const p    = viewingPlayer;
        const risk = getRisk(p.ciLow, p.ciHigh, p.proj);
        const img  = getPlayerImage(p);
        const initials = p.name.split(' ').map(n => n[0]).join('').slice(0, 2);
        const spread   = p.ciLow != null && p.ciHigh != null ? (p.ciHigh - p.ciLow) : null;
        // Position of the projection marker along the CI range bar
        const projPct  = spread > 0 ? ((p.proj - p.ciLow) / spread) * 100 : 50;
        const isFav    = favoritedIds.includes(p.id);

        const POS_HEX = { QB:'#f87171', RB:'#4ade80', WR:'#60a5fa', TE:'#facc15', K:'#c084fc', DST:'#fb923c' };
        const posHex  = POS_HEX[p.pos] || '#ffffff';

        return (
          <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center p-0 sm:p-4"
            onClick={() => setViewingPlayer(null)}>
            <div className="absolute inset-0 bg-black/75 backdrop-blur-sm"/>

            <div
              className="relative w-full sm:max-w-sm bg-[#0a1020] border border-white/[0.09] rounded-t-3xl sm:rounded-3xl shadow-[0_32px_80px_rgba(0,0,0,0.95)] flex flex-col overflow-hidden"
              onClick={e => e.stopPropagation()}
            >
              {/* Hero header with player photo / gradient */}
              <div className="relative h-36 flex-shrink-0 overflow-hidden">
                {img ? (
                  <img src={img} alt={p.name}
                    className="absolute inset-0 w-full h-full object-cover object-center"
                    style={{ filter: 'brightness(0.55) saturate(1.2)' }}
                    onError={e => { e.target.style.display = 'none'; }}
                  />
                ) : (
                  <div className="absolute inset-0"
                    style={{ background: `radial-gradient(ellipse at 30% 50%, ${posHex}22 0%, #0a1020 70%)` }}/>
                )}
                {/* Bottom fade so text is readable over the image */}
                <div className="absolute inset-x-0 bottom-0 h-20"
                  style={{ background: 'linear-gradient(to top, #0a1020, transparent)' }}/>
                <button
                  onClick={() => setViewingPlayer(null)}
                  className="absolute top-3 right-3 w-7 h-7 rounded-full bg-black/40 hover:bg-black/60 flex items-center justify-center transition-all backdrop-blur-sm"
                >
                  <X size={13} className="text-white/70"/>
                </button>
                <button
                  onClick={() => {
                    const updated = toggleFavorite(p);
                    setFavoritedIds(updated.map(f => f.id));
                  }}
                  className="absolute top-3 right-12 w-7 h-7 rounded-full bg-black/40 hover:bg-black/60 flex items-center justify-center transition-all backdrop-blur-sm"
                >
                  <Heart size={13} className={isFav ? 'text-red-400 fill-red-400' : 'text-white/50'}/>
                </button>
                {/* Player name + position overlay */}
                <div className="absolute bottom-0 left-0 right-0 px-5 pb-4">
                  <div className="flex items-end gap-3">
                    {!img && (
                      <div className="w-12 h-12 rounded-2xl flex items-center justify-center text-base font-black flex-shrink-0 mb-0.5"
                        style={{ background: `${posHex}22`, border: `1.5px solid ${posHex}40`, color: posHex }}>
                        {initials}
                      </div>
                    )}
                    <div>
                      <h2 className="text-lg font-black leading-tight text-white drop-shadow-lg">{p.name}</h2>
                      <div className="flex items-center gap-2 mt-0.5">
                        <span className="text-[10px] font-black px-2 py-0.5 rounded-md"
                          style={{ background: `${posHex}25`, color: posHex }}>
                          {p.pos}
                        </span>
                        <span className="text-xs text-white/50 font-medium">{p.team}</span>
                        <span className="text-white/20 text-xs">·</span>
                        <span className="text-xs text-white/35">Wk {selectedWeek} · {season}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Projection number + floor/ceiling tiles */}
              <div className="px-5 py-5 flex items-center gap-5 border-b border-white/[0.06]">
                <div className="text-center flex-shrink-0">
                  <div className="text-4xl font-black text-[#4d7cff] leading-none">{p.proj ?? '—'}</div>
                  <div className="text-[9px] text-white/25 uppercase tracking-widest mt-1">Projected</div>
                </div>
                <div className="w-px h-12 bg-white/[0.07] flex-shrink-0"/>
                <div className="flex-1 grid grid-cols-2 gap-3">
                  <div className="bg-green-500/[0.07] border border-green-500/[0.15] rounded-xl px-3 py-2.5">
                    <div className="text-base font-black text-green-400">{formatCI(p.ciLow)}</div>
                    <div className="text-[9px] text-white/25 uppercase tracking-widest mt-0.5">Floor</div>
                  </div>
                  <div className="bg-yellow-500/[0.07] border border-yellow-500/[0.15] rounded-xl px-3 py-2.5">
                    <div className="text-base font-black text-yellow-400">{formatCI(p.ciHigh)}</div>
                    <div className="text-[9px] text-white/25 uppercase tracking-widest mt-0.5">Ceiling</div>
                  </div>
                </div>
              </div>

              {/* CI range bar with projection marker */}
              <div className="px-5 py-4 border-b border-white/[0.06]">
                <div className="flex items-center justify-between mb-2.5">
                  <span className="text-[10px] text-white/30 uppercase tracking-widest font-bold">Range</span>
                  <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${risk.bg} ${risk.color}`}>
                    {risk.label} Risk{spread != null ? ` · ${spread.toFixed(1)} pt spread` : ''}
                  </span>
                </div>
                {spread != null ? (
                  <div>
                    <div className="relative h-5 flex items-center">
                      <div className="absolute inset-x-0 h-2.5 rounded-full"
                        style={{ background: 'linear-gradient(to right, rgba(248,113,113,0.7), rgba(77,124,255,0.9) 50%, rgba(74,222,128,0.7))' }}/>
                      {/* Needle sits at projPct along the range */}
                      <div
                        className="absolute -translate-x-1/2 w-4 h-4 rounded-full bg-[#4d7cff] border-2 border-[#0a1020] shadow-[0_0_10px_rgba(77,124,255,0.9)] z-10"
                        style={{ left: `${Math.max(5, Math.min(projPct, 95))}%` }}
                      />
                    </div>
                    <div className="flex justify-between mt-1.5">
                      <span className="text-[10px] text-red-400/70 font-bold">{formatCI(p.ciLow)}</span>
                      <span className="text-[10px] text-[#4d7cff] font-black">{p.proj} proj</span>
                      <span className="text-[10px] text-green-400/70 font-bold">{formatCI(p.ciHigh)}</span>
                    </div>
                  </div>
                ) : (
                  <div className="text-white/20 text-xs text-center py-2">No confidence interval available</div>
                )}
              </div>

              {/* Quick-action buttons: Trends, Compare, Add to Team */}
              <div className="px-5 py-4 grid grid-cols-3 gap-2">
                <button
                  onClick={() => {
                    setViewingPlayer(null);
                    navigate('/analytics', { state: { player: {
                      player_id:   p.id,
                      player_name: p.name,
                      position:    p.pos,
                      team_code:   p.team,
                      headshot_url: p.imageUrl || null,
                    }}});
                  }}
                  className="flex flex-col items-center gap-1.5 py-3 rounded-xl bg-white/[0.03] hover:bg-white/[0.06] border border-white/[0.06] transition-all"
                >
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" className="text-[#4d7cff]">
                    <polyline points="1,12 5,7 9,9 15,3"/>
                  </svg>
                  <span className="text-[10px] text-white/40">Trends</span>
                </button>
                <button
                  onClick={() => {
                    navigate('/compare', { state: { playerIds: [p.id] } });
                    setViewingPlayer(null);
                  }}
                  className="flex flex-col items-center gap-1.5 py-3 rounded-xl bg-white/[0.03] hover:bg-white/[0.06] border border-white/[0.06] transition-all"
                >
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" className="text-purple-400">
                    <rect x="1" y="3" width="5" height="10" rx="1"/><rect x="10" y="3" width="5" height="10" rx="1"/>
                  </svg>
                  <span className="text-[10px] text-white/40">Compare</span>
                </button>
                <button
                  onClick={() => {
                    navigate('/team-builder', { state: { player: p } });
                    setViewingPlayer(null);
                  }}
                  className="flex flex-col items-center gap-1.5 py-3 rounded-xl bg-white/[0.03] hover:bg-white/[0.06] border border-white/[0.06] transition-all"
                >
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" className="text-green-400">
                    <path d="M8 1v14M1 8h14"/>
                  </svg>
                  <span className="text-[10px] text-white/40">Add to Team</span>
                </button>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
};

export default Dashboard;
